import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FUENTE, useTema } from './tema';

/**
 * ComboDeCatalogo — escribir, o elegir de lo que ya se escribió.
 *
 * El campo «Lugar» es el caso que lo motiva. Se escriben cuatro o cinco sitios
 * una y otra vez, y cada vez de una forma distinta: «Sala de capacitación»,
 * «sala capacitacion», «Sala Capacitación 2do piso». Tres actas del mismo sitio
 * que luego no se pueden agrupar ni encontrar.
 *
 * El campo NO se teclea: se elige. Escribir libremente es lo que producía esas
 * variantes, así que lo que se puede poner en el acta es lo que está en la
 * lista y nada más.
 *
 * Lo que salva a esto de ser un desplegable cerrado —donde el día que hay
 * reunión en un sitio nuevo el acta no se puede escribir hasta que alguien
 * mantenga la lista— es el «+» de al lado: cualquiera da de alta un lugar en el
 * momento, sin salir del acta ni pedirle permiso a nadie. La lista es cerrada
 * para escribir y abierta para crecer.
 *
 * ── Administrar la lista, sin salir de aquí ─────────────────────────────
 *
 * Al lado va un «+» que abre una ventanita para dar de alta un valor con su
 * nombre bien puesto, sin tener que escribirlo en el acta primero. Y cada
 * sugerencia lleva un lápiz que abre esa misma ventanita para corregir el
 * nombre o borrarla.
 *
 * Corregir no es borrar y volver a crear: el valor conserva su cuenta de usos,
 * que es lo que ordena las sugerencias. Arreglarle una tilde al sitio de
 * siempre no debe mandarlo al último puesto de la lista. Y si el nombre nuevo
 * ya existía, los dos se funden —que es exactamente lo que se pide cuando
 * alguien renombra «sala capacitacion» teniendo también «Sala de
 * capacitación»—.
 *
 * ── Por qué no un `<datalist>` ──────────────────────────────────────────
 *
 * Sería una línea de HTML, pero su desplegable lo pinta el navegador: no se
 * puede dar estilo, cambia de un navegador a otro, ignora los colores del tema
 * —blanco deslumbrante sobre la pantalla oscura— y en varios no filtra por
 * coincidencia parcial sino solo por el principio del texto. Buscar «capa» no
 * encontraría «Sala de capacitación», que es justo lo que se busca. Y no hay
 * donde colgarle un lápiz.
 */

const estilos = (C) => ({
  caja: { position: 'relative', display: 'flex', gap: 6, alignItems: 'stretch' },

  nuevo: {
    flexShrink: 0, width: 34, borderRadius: 7, cursor: 'pointer',
    background: C.panel, color: C.suave, border: `1px solid ${C.borde}`,
    fontSize: 17, lineHeight: 1, fontFamily: FUENTE
  },

  lista: {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 40, zIndex: 30,
    maxHeight: 226, overflowY: 'auto', padding: 4,
    background: C.panelAlto, border: `1px solid ${C.borde}`, borderRadius: 9,
    boxShadow: '0 10px 30px rgba(0,0,0,.34)'
  },
  opcion: (resaltada) => ({
    display: 'flex', alignItems: 'center', gap: 6, width: '100%',
    padding: '6px 8px', borderRadius: 6,
    background: resaltada ? C.panel : 'transparent', color: C.texto
  }),
  texto: {
    flex: 1, minWidth: 0, background: 'transparent', border: 'none',
    padding: 0, cursor: 'pointer', textAlign: 'left',
    color: 'inherit', fontSize: 13.5, fontFamily: FUENTE,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
  },
  buscador: {
    width: '100%', boxSizing: 'border-box', marginBottom: 4,
    padding: '6px 9px', borderRadius: 6, background: C.panel, color: C.texto,
    border: `1px solid ${C.borde}`, fontSize: 13, fontFamily: FUENTE
  },
  marca: { fontSize: 12, color: C.acentoClaro, flexShrink: 0 },
  vacio: { padding: '8px 9px', fontSize: 12.5, color: C.tenue, lineHeight: 1.5 },
  usos: { fontSize: 11.5, color: C.tenue, flexShrink: 0 },
  lapiz: {
    background: 'transparent', border: 'none', padding: '3px 5px', cursor: 'pointer',
    color: C.tenue, fontSize: 12, lineHeight: 1, borderRadius: 4, flexShrink: 0
  },

  // ── La ventanita de alta y edición ──
  velo: {
    position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24
  },
  ventana: {
    width: '100%', maxWidth: 420, background: C.fondo, color: C.texto,
    border: `1px solid ${C.borde}`, borderRadius: 12, padding: 20,
    boxShadow: '0 22px 60px rgba(0,0,0,.5)', fontFamily: FUENTE
  },
  titulo: { margin: '0 0 4px', fontSize: 16, fontWeight: 600 },
  ayuda: { margin: '0 0 14px', fontSize: 12.5, color: C.tenue, lineHeight: 1.5 },
  campoVentana: {
    width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 8,
    background: C.panel, color: C.texto, border: `1px solid ${C.borde}`,
    fontSize: 14, fontFamily: FUENTE
  },
  error: { margin: '9px 0 0', fontSize: 12.5, color: C.error, lineHeight: 1.45 },
  botones: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 18 },
  boton: {
    padding: '8px 15px', borderRadius: 8, border: `1px solid ${C.borde}`,
    background: C.panel, color: C.texto, fontSize: 13.5, fontFamily: FUENTE, cursor: 'pointer'
  },
  botonAcento: { background: C.acento, borderColor: C.acento, color: '#fff' },
  botonPeligro: { marginRight: 'auto', color: C.error, borderColor: C.error }
});

const normalizar = (t) => String(t || '')
  .toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * @param {Object} props
 * @param {string} props.valor
 * @param {Function} props.onCambiar - recibe el texto
 * @param {Array} props.opciones - [{ valor, usos }]
 * @param {Function} [props.onCrear] - alta desde la ventanita
 * @param {Function} [props.onRenombrar] - (antes, despues)
 * @param {Function} [props.onOlvidar] - quitar del catálogo
 * @param {Object} [props.estilo] - el del campo, para que mida como los demás
 * @param {string} [props.placeholder]
 * @param {boolean} [props.soloSeleccion] - el campo no se teclea, se elige
 */
const ComboDeCatalogo = ({
  valor = '', onCambiar, opciones = [],
  onCrear = null, onRenombrar = null, onOlvidar = null,
  estilo = {}, placeholder, soloSeleccion = false
}) => {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  // `aria-controls` necesita un id estable que apunte a la lista: es lo que
  // permite a un lector de pantalla anunciar cuántas sugerencias hay.
  const idLista = useRef(`catalogo-${Math.random().toString(36).slice(2, 9)}`).current;

  const [abierto, setAbierto] = useState(false);
  const [resaltada, setResaltada] = useState(-1);
  const caja = useRef(null);

  // La ventanita: `null` cerrada; `{ original }` con original vacío es un alta.
  const [ficha, setFicha] = useState(null);
  const [nombre, setNombre] = useState('');
  const [fallo, setFallo] = useState(null);
  const [trabajando, setTrabajando] = useState(false);

  // Cuando el campo no se teclea hace falta otro sitio donde buscar: una caja
  // dentro del desplegable, que aparece solo si la lista es larga. Con seis
  // lugares, buscar sobra; con cuarenta, es imprescindible.
  const [busqueda, setBusqueda] = useState('');
  const BUSCADOR_DESDE = 8;
  const conBuscador = soloSeleccion && opciones.length >= BUSCADOR_DESDE;

  // Se filtra por coincidencia en cualquier parte y sin tildes: quien busca su
  // sala escribe «capa», no «Sala de ca».
  const filtradas = useMemo(() => {
    const texto = normalizar(soloSeleccion ? busqueda : valor).trim();
    const lista = opciones.filter(o => o && o.valor);
    if (!texto) return lista;
    return lista.filter(o => normalizar(o.valor).includes(texto));
  }, [opciones, valor, busqueda, soloSeleccion]);

  // Tecleando, lo que ya está escrito exactamente igual no se ofrece: sugerir
  // lo que la persona acaba de terminar de escribir es ruido. Eligiendo, en
  // cambio, la lista se enseña entera —incluido lo ya elegido, marcado— porque
  // es la única forma de ver qué hay.
  const sugerencias = useMemo(
    () => (soloSeleccion
      ? filtradas
      : filtradas.filter(o => normalizar(o.valor) !== normalizar(valor).trim())),
    [filtradas, valor, soloSeleccion]
  );

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  useEffect(() => { setResaltada(-1); }, [valor]);

  const elegir = (texto) => {
    onCambiar(texto);
    setAbierto(false);
    setResaltada(-1);
    // La próxima vez que se abra, la lista entera: una búsqueda vieja dejaría
    // media lista escondida sin que se vea por qué.
    setBusqueda('');
  };

  const abrirFicha = (original) => {
    setAbierto(false);
    setFicha({ original });
    // El alta arranca EN BLANCO.
    //
    // Antes proponía lo que hubiera escrito en el campo, con la idea de ahorrar
    // teclas. Salía al revés: si ese texto era un lugar que ya existía, guardar
    // no daba de alta nada —le sumaba un uso al de siempre— y parecía que «+»
    // sobrescribía en vez de crear. «Nuevo» tiene que empezar vacío.
    setNombre(original || '');
    setFallo(null);
  };

  const cerrarFicha = () => { setFicha(null); setFallo(null); setTrabajando(false); };

  const guardarFicha = async () => {
    const limpio = nombre.trim().replace(/\s+/g, ' ');
    if (!limpio) { setFallo('Escribe un nombre.'); return; }

    const original = ficha.original;
    // Se compara sin tildes ni mayúsculas porque es como lo compara la base:
    // para ella «Sala A» y «sala a» son el mismo valor, y dejar pasar el alta
    // acabaría sumándole un uso al que ya estaba en vez de crear nada.
    const repetido = opciones.some(
      o => normalizar(o.valor) === normalizar(limpio) && o.valor !== original
    );
    // Dar de alta uno que ya está no se deja: no crearía nada y confundiría.
    // Renombrar a uno que ya está sí, y ahí los dos se funden sumando sus usos:
    // es justo lo que se busca al corregir la variante mal escrita de un sitio
    // que también existe bien escrito.
    if (repetido && !original) { setFallo('Ese ya está en la lista.'); return; }

    setTrabajando(true);
    setFallo(null);
    try {
      if (original) {
        if (onRenombrar) await onRenombrar(original, limpio);
        // Si el acta llevaba escrito el nombre viejo, se actualiza: dejarlo con
        // el anterior haría que corregirlo en la lista no corrigiera el acta.
        if (valor.trim() === original) onCambiar(limpio);
      } else {
        if (onCrear) await onCrear(limpio);
        onCambiar(limpio);
      }
      cerrarFicha();
    } catch (e) {
      setFallo(e.mensajeUsuario || e.message || 'No se pudo guardar.');
      setTrabajando(false);
    }
  };

  const borrarFicha = async () => {
    setTrabajando(true);
    try {
      if (onOlvidar) await onOlvidar(ficha.original);
      cerrarFicha();
    } catch (e) {
      setFallo(e.mensajeUsuario || e.message || 'No se pudo quitar.');
      setTrabajando(false);
    }
  };

  const alPulsarTecla = (e) => {
    if (e.key === 'Escape') { setAbierto(false); setBusqueda(''); return; }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!sugerencias.length) return;
      e.preventDefault();
      if (!abierto) { setAbierto(true); return; }
      const paso = e.key === 'ArrowDown' ? 1 : -1;
      setResaltada(i => {
        const siguiente = i + paso;
        if (siguiente < 0) return sugerencias.length - 1;
        if (siguiente >= sugerencias.length) return 0;
        return siguiente;
      });
      return;
    }

    // Enter solo elige cuando hay una opción marcada con las flechas. Si no,
    // tecleando vale lo escrito —es una caja de texto— y eligiendo no hace
    // nada, porque no hay nada que confirmar.
    if (e.key === 'Enter' && abierto && resaltada >= 0 && sugerencias[resaltada]) {
      e.preventDefault();
      elegir(sugerencias[resaltada].valor);
    }
  };

  return (
    <div style={S.caja} ref={caja}>
      <input
        style={{
          ...estilo, flex: 1, minWidth: 0,
          ...(soloSeleccion ? { cursor: 'pointer' } : {})
        }}
        value={valor}
        placeholder={placeholder}
        readOnly={soloSeleccion}
        onChange={soloSeleccion ? undefined : (e) => { onCambiar(e.target.value); setAbierto(true); }}
        onFocus={() => setAbierto(true)}
        // Elegir tiene que costar un clic, no dos: sin esto, abrir el
        // desplegable y volverlo a abrir requiere pasar por otro sitio.
        onClick={soloSeleccion ? () => setAbierto(a => !a) : undefined}
        onKeyDown={alPulsarTecla}
        role="combobox"
        aria-expanded={abierto}
        aria-controls={idLista}
        aria-autocomplete={soloSeleccion ? 'none' : 'list'}
      />

      {onCrear && (
        <button
          type="button"
          style={S.nuevo}
          onClick={() => abrirFicha('')}
          title="Añadir a la lista"
          aria-label="Añadir a la lista"
        >
          +
        </button>
      )}

      {abierto && (sugerencias.length > 0 || valor.trim() || soloSeleccion) && (
        <div style={S.lista} id={idLista} role="listbox">
          {conBuscador && (
            <input
              style={S.buscador}
              value={busqueda}
              autoFocus
              placeholder="Buscar…"
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={alPulsarTecla}
              aria-label="Buscar en la lista"
            />
          )}
          {sugerencias.map((o, i) => (
            <div key={o.valor} style={S.opcion(i === resaltada)}>
              <button
                type="button"
                style={S.texto}
                onMouseEnter={() => setResaltada(i)}
                onClick={() => elegir(o.valor)}
                role="option"
                aria-selected={i === resaltada}
              >
                {o.valor}
              </button>
              {soloSeleccion && normalizar(o.valor) === normalizar(valor) && (
                <span style={S.marca} aria-hidden="true">✓</span>
              )}
              {o.usos > 1 && <span style={S.usos}>{o.usos}</span>}
              {(onRenombrar || onOlvidar) && (
                <button
                  type="button"
                  style={S.lapiz}
                  title="Editar o quitar"
                  aria-label={`Editar "${o.valor}"`}
                  onClick={() => abrirFicha(o.valor)}
                >
                  ✎
                </button>
              )}
            </div>
          ))}

          {sugerencias.length === 0 && (
            <div style={S.vacio}>
              {soloSeleccion
                ? (opciones.length === 0
                  ? 'Todavía no hay ninguno. Pulsa «+» para dar de alta el primero.'
                  : 'Ninguno coincide. Pulsa «+» para dar de alta uno nuevo.')
                : 'Nuevo. Al guardar el acta se añade a la lista, o pulsa «+» para darlo de alta ahora.'}
            </div>
          )}
        </div>
      )}

      {ficha && (
        <div style={S.velo} onMouseDown={cerrarFicha}>
          <div
            style={S.ventana}
            role="dialog"
            aria-modal="true"
            aria-label={ficha.original ? 'Editar el lugar' : 'Nuevo lugar'}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 style={S.titulo}>{ficha.original ? 'Editar' : 'Nuevo'}</h3>
            <p style={S.ayuda}>
              {ficha.original
                ? 'Cambiarle el nombre lo cambia en la lista y conserva las veces que se ha usado.'
                : 'Queda en tu lista y se ofrecerá al escribir este campo.'}
            </p>

            <input
              style={S.campoVentana}
              value={nombre}
              autoFocus
              onChange={(e) => setNombre(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); guardarFicha(); }
                if (e.key === 'Escape') cerrarFicha();
              }}
              aria-label="Nombre"
            />

            {fallo && <p style={S.error}>{fallo}</p>}

            <div style={S.botones}>
              {ficha.original && onOlvidar && (
                <button
                  type="button"
                  style={{ ...S.boton, ...S.botonPeligro }}
                  onClick={borrarFicha}
                  disabled={trabajando}
                >
                  Eliminar
                </button>
              )}
              <button type="button" style={S.boton} onClick={cerrarFicha} disabled={trabajando}>
                Cancelar
              </button>
              <button
                type="button"
                style={{ ...S.boton, ...S.botonAcento }}
                onClick={guardarFicha}
                disabled={trabajando}
              >
                {trabajando ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComboDeCatalogo;
